import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Pencil, Ban } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient, { get, post, patch, del } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from './shared.js';
import FiltroFechas from './FiltroFechas.jsx';
import { RESOURCES_FINANZAS_CXP } from '../../config/resources.finanzas-cxp.js';
import { buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

const cfg = RESOURCES_FINANZAS_CXP['gastos-contenedor'];

function valoresIniciales(row) {
  if (!row) {
    return {
      fecha: today(), contenedores: '', proveedores: '', clientes: '', conceptosGastos: '', descripcion: 'NA',
      moneda: 'MXN', totalUSD: 0, tipoCambio: 1, total: 0,
    };
  }
  return {
    fecha: row.fecha, contenedores: row._idContenedor, proveedores: row._idProveedor, clientes: row._idCliente,
    conceptosGastos: row._idConcepto, descripcion: row.descripcion, moneda: row.moneda || 'MXN',
    totalUSD: row.totalUSD, tipoCambio: row.tipoCambio, total: row.total,
  };
}

function GastoContenedorForm({ open, onClose, title, initial, proveedores, conceptos, contenedores, onSubmit, esEdicion }) {
  const [v, setV] = useState(valoresIniciales(initial));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales(initial)); }, [open, initial]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  function seleccionaContenedor(id) {
    const c = contenedores.find((x) => x._id === id);
    const cliente = primero(c?.clientes);
    setV((s) => ({ ...s, contenedores: id, clientes: cliente?._id || '' }));
  }

  function cambiaMoneda(m) { setV((s) => ({ ...s, moneda: m, totalUSD: 0, tipoCambio: 1, total: 0 })); }
  function cambiaTotalUSD(val) { const totalUSD = Number(val) || 0; setV((s) => ({ ...s, totalUSD, total: totalUSD * (Number(s.tipoCambio) || 0) })); }
  function cambiaTipoCambio(val) { const tipoCambio = Number(val) || 0; setV((s) => ({ ...s, tipoCambio, total: (Number(s.totalUSD) || 0) * tipoCambio })); }
  function cambiaTotalMXN(val) { const total = Number(val) || 0; setV((s) => ({ ...s, total, totalUSD: s.tipoCambio ? total / s.tipoCambio : 0 })); }

  const clienteNombre = (() => {
    const c = contenedores.find((x) => x._id === v.contenedores);
    return primero(c?.clientes)?.nombre_comercial || '—';
  })();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.proveedores || !v.conceptosGastos || !v.fecha || !v.contenedores) {
      Swal.fire({ icon: 'error', title: 'Faltan datos', text: 'Fecha, contenedor, proveedor y concepto son obligatorios' });
      return;
    }
    setGuardando(true);
    try {
      await onSubmit(v);
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="gasto-cont-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="gasto-cont-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required disabled={esEdicion} value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Contenedor</label>
          <select required disabled={esEdicion} value={v.contenedores} onChange={(e) => seleccionaContenedor(e.target.value)}
            className="select-base">
            <option value="">Selecciona...</option>
            {[...contenedores].sort((a, b) => (a.numeroInterno > b.numeroInterno ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.numeroInterno}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Proveedor</label>
          <select required value={v.proveedores} onChange={(e) => set('proveedores', e.target.value)}
            className="select-base">
            <option value="">Selecciona...</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Cliente</label>
          <input type="text" disabled value={clienteNombre} className="input-base" />
        </div>
        <div>
          <label className="label">Concepto</label>
          <select required value={v.conceptosGastos} onChange={(e) => set('conceptosGastos', e.target.value)}
            className="select-base">
            <option value="">Selecciona...</option>
            {[...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Descripción</label>
          <input type="text" required value={v.descripcion} onChange={(e) => set('descripcion', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Moneda</label>
          <select value={v.moneda} onChange={(e) => cambiaMoneda(e.target.value)}
            className="select-base">
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>
        {v.moneda === 'USD' ? (
          <>
            <div>
              <label className="label">Total USD</label>
              <input type="number" min="0" step="any" required value={v.totalUSD} onChange={(e) => cambiaTotalUSD(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" min="0" step="any" required value={v.tipoCambio} onChange={(e) => cambiaTipoCambio(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Total MXN</label>
              <input type="number" disabled value={v.total.toFixed(2)} className="input-base" />
            </div>
          </>
        ) : (
          <div>
            <label className="label">Total MXN</label>
            <input type="number" min="0" step="any" required value={v.total} onChange={(e) => cambiaTotalMXN(e.target.value)}
              className="input-base" />
          </div>
        )}
      </form>
    </Modal>
  );
}

export default function GastosContenedorPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${cfg.recurso}:ver`);
  const puedeCrear = hasPermiso(`${cfg.recurso}:crear`);
  const puedeEditar = hasPermiso(`${cfg.recurso}:editar`);
  const puedeEliminar = hasPermiso(`${cfg.recurso}:eliminar`);

  const [gastos, setGastos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [contenedores, setContenedores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());
  const [mailOpen, setMailOpen] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [gastosData, provData, concData, contData] = await Promise.all([
        get(`/${cfg.route}/fechas/${fechaInicio}/${fechaFin}`),
        apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => r.data.data),
        apiClient.get('/conceptos-gastos', { params: { limit: 500 } }).then((r) => r.data.data),
        apiClient.get('/contenedores', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setProveedores(provData || []);
      setConceptos(concData || []);
      setContenedores(contData || []);
      setGastos((gastosData || []).map((g) => {
        const prov = primero(g.proveedores);
        const conc = primero(g.conceptosGastos);
        const cont = primero(g.contenedores);
        const cli = primero(g.clientes);
        return {
          _id: g._id,
          numero: g.idGastoContenedor,
          fecha: g.fecha,
          descripcion: g.descripcion,
          concepto: conc?.name || '—',
          _idConcepto: conc?._id,
          proveedor: prov?.name || '—',
          _idProveedor: prov?._id,
          contenedor: cont?.numeroInterno || '—',
          _idContenedor: cont?._id,
          cliente: cli?.nombre_comercial || '—',
          _idCliente: cli?._id,
          totalUSD: g.totalUSD,
          tipoCambio: g.tipoCambio,
          total: g.total,
          saldo: g.saldo,
          moneda: g.moneda,
          is_active: g.is_active,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(gastos, {
    searchFields: ['concepto', 'proveedor', 'contenedor', 'cliente', 'descripcion', 'total', 'tipoCambio'],
    defaultSortBy: 'fecha', defaultSortOrder: 'desc', perPage: 50,
  });

  // Columnas EXACTAS de V1 (`ListadoGastosContenedor.jsx` -> `PDFTabla()`/`excel()`):
  // la función es un copy-paste literal de la de Gastos -- solo exporta Fecha,
  // Concepto, Descripcion e Importe, SIN Contenedor/Cliente/Proveedor/Número/
  // Saldo/Estatus, aunque esas sí aparecen en la tabla en pantalla de V1.
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'concepto', label: 'Concepto' },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'total', label: 'Importe', render: (r) => formatMoney(r.total) },
  ];
  const columnasExcel = columnasExport.map((c) => (c.key === 'total' ? { key: 'total', label: 'Importe' } : c));
  const totalExport = filtradas.reduce((a, r) => a + (r.total || 0), 0);

  const armarPdf = () => buildTablePdf('Detalle de Gastos', columnasExport, filtradas, { totalCols: { descripcion: 'Total', total: formatMoney(totalExport) } });

  async function guardar(valores) {
    if (editando) {
      const abonos = Number(editando.total || 0) - Number(editando.saldo || 0);
      if (Number(valores.total) < abonos) {
        throw new Error(`El total ($${valores.total}) no puede ser menor que los abonos existentes ($${abonos.toFixed(2)})`);
      }
    }
    const payload = {
      fecha: valores.fecha,
      totalUSD: Number(valores.totalUSD),
      tipoCambio: Number(valores.tipoCambio),
      total: Number(valores.total),
      descripcion: valores.descripcion,
      conceptosGastos: valores.conceptosGastos,
      contenedores: valores.contenedores,
      proveedores: valores.proveedores,
      clientes: valores.clientes || undefined,
      moneda: valores.moneda,
    };
    if (editando) await patch(`/${cfg.route}/${editando._id}`, payload);
    else await post(`/${cfg.route}`, { ...payload, abonos: 0, saldo: payload.total });
    await cargar();
  }

  async function cancelarGasto(row) {
    const r = await Swal.fire({
      title: '¿Estás seguro?', text: '¡Se cancelará el Gasto Contenedor!', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Sí, Cancelar!',
    });
    if (!r.isConfirmed) return;
    try {
      await del(`/${cfg.route}/${row._id}`);
      Swal.fire('Listo', 'Gasto de contenedor cancelado', 'success');
      await cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: e.message });
    }
  }

  const columnas = [
    { key: 'numero', label: 'Número', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'concepto', label: 'Concepto', sortable: true },
    { key: 'descripcion', label: 'Descripción', sortable: true },
    { key: 'contenedor', label: 'Contenedor', sortable: true },
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'proveedor', label: 'Proveedor', sortable: true },
    { key: 'tipoCambio', label: 'TC', sortable: true },
    { key: 'total', label: 'Importe', sortable: true, render: (r) => formatMoney(r.total) },
    {
      key: '__acciones', label: 'Editar', sortable: false,
      render: (row) => {
        if (row.is_active === 'No') return 'CANCELADO';
        const cancelable = puedeEliminar && row.saldo === row.total;
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              title="Cancelar" aria-label="Cancelar" disabled={!cancelable}
              className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => cancelarGasto(row)}
            >
              <Ban size={15} />
            </button>
            {puedeEditar && (
              <button
                title="Editar" aria-label="Editar"
                className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50"
                onClick={() => { setEditando(row); setFormOpen(true); }}
              >
                <Pencil size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{cfg.title}</h1>
        {puedeCrear && (
          <button onClick={() => { setEditando(null); setFormOpen(true); }} className="btn-primary">
            + Nuevo Gasto de Contenedor
          </button>
        )}
      </div>

      <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
          className="input-base w-64" />
        <button type="button" onClick={async () => (await armarPdf()).save('DetalleGastos.pdf')}
          className="btn-outline text-xs px-3 py-1.5">PDF</button>
        <button type="button" onClick={() => setMailOpen(true)}
          className="btn-outline text-xs px-3 py-1.5">eMail</button>
        <button type="button" onClick={() => exportTableExcel('Detalle de Gastos', columnasExcel, filtradas, { fileName: `DetalleGastos-${fechaInicio}-${fechaFin}`, sheetName: 'DetalleGastos' })}
          className="btn-outline text-xs px-3 py-1.5">Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ tipoCambio: 'TOTAL', total: formatMoney(pageRows.reduce((a, r) => a + (r.total || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Gastos Contenedor"
        onEnviar={async (email) => {
          const doc = await armarPdf();
          await enviarCorreoAdjunto({
            subject: 'Lista de Gastos', email, fileName: 'ListaGastos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Gastos.'),
          });
        }}
      />

      <GastoContenedorForm
        open={formOpen} onClose={() => setFormOpen(false)}
        title={editando ? 'Editar Gasto' : 'Nuevo Gasto'}
        initial={editando} proveedores={proveedores} conceptos={conceptos} contenedores={contenedores}
        onSubmit={guardar} esEdicion={!!editando}
      />
    </div>
  );
}
